import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { config } from '@/config'
import { useAuthStore, selectIsAuthenticated } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card'
import { LocaleToggle } from '@/components/low/LocaleToggle'
import { CoinBalance } from '@/components/medium/CoinBalance'
import { updateLocale, getGameModes, type GameModeListItem } from '@/services/api'
import type { Locale } from '@/types/auth'
import { detectSupportedLocale } from '@/types/auth'
import { EmojiText } from '../components/low/AnimatedEmoji'
import { PixelIcon } from '@/components/low/PixelIcon'
import { BrandName } from '@/components/low/BrandName'

interface MetaChipProps {
  /** Emoji standing in for the metric; decorative, the label carries the meaning. */
  icon: string
  /** Accessible name of the metric, e.g. "Number of players". */
  label: string
  value: string | number
}

/**
 * Compact metric tile shown next to a game mode's title.
 *
 * The value is set in font-sans rather than the pixel display face: these are
 * dense digits and short units, which the pixel face renders illegibly at this
 * size. The icon stays pixel-art regardless — it comes from the emoji font,
 * which is selected per codepoint and so is unaffected by the text face.
 */
function MetaChip({ icon, label, value }: MetaChipProps) {
  return (
    <span
      title={label}
      aria-label={`${label}: ${value}`}
      className="inline-flex items-center gap-1 border-2 border-border bg-muted px-1.5 py-0.5 font-sans text-[11px] font-bold leading-none text-foreground"
    >
      <PixelIcon>{icon}</PixelIcon>
      {value}
    </span>
  )
}

export function HomePage() {
  const navigate = useNavigate()
  // `null` means "not fetched yet"; loading is derived so the fetch effect never
  // calls setState synchronously (react-hooks/set-state-in-effect).
  const [games, setGames] = useState<GameModeListItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const loading = games === null && error === null
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { t, i18n } = useTranslation() // t: UI copy; i18n: subscribes to language changes for re-render
  const isAuthenticated = useAuthStore(selectIsAuthenticated)
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const { logout } = useAuthStore()

  // Current locale: from user if logged in, else from i18next (navigator-detected)
  const currentLocale: Locale = user !== null && user !== undefined
    ? user.locale
    : detectSupportedLocale(i18n.language)

  const handleLocaleToggle = async (newLocale: Locale) => {
    if (newLocale === currentLocale) return
    i18n.changeLanguage(newLocale)
    if (isAuthenticated && user) {
      try {
        const updatedUser = await updateLocale(newLocale)
        setUser(updatedUser)
      } catch {
        // Revert language on API failure
        i18n.changeLanguage(currentLocale)
      }
    }
  }

  const handleLogout = async () => {
    setDropdownOpen(false)
    await logout()
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  useEffect(() => {
    let cancelled = false
    getGameModes(currentLocale)
      .then((data) => {
        if (cancelled) return
        setError(null)
        setGames(data.modes)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Error fetching game modes:', err)
        setError(tRef.current('home.loadGamesFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [currentLocale])

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <div className="max-w-7xl mx-auto px-6 pt-4 pb-12 md:pt-6 md:pb-20 w-full flex flex-col flex-1">
        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-center mb-20 gap-8">
          <div className="flex-1 text-center">
            <div className="flex items-center justify-center">
              {/* The logo images are decorative: the heading's text equivalent
                  is the adjacent screen-reader-only wordmark, so alt text here
                  would only duplicate the accessible name. */}
              <h1 className="flex items-end justify-center">
                {/* Mobile: combined logo (TV above wordmark) */}
                <img
                  src="/assets/logo/logo.webp"
                  alt=""
                  className="h-40 w-auto max-w-full md:hidden"
                />
                {/* Desktop: TV left, wordmark right — lower total height */}
                <img
                  src="/assets/logo/logo-tv.webp"
                  alt=""
                  className="hidden md:block h-28 w-auto"
                />
                <img
                  src="/assets/logo/logo-napis.webp"
                  alt=""
                  className="hidden md:block h-16 w-auto max-w-full pl-2 pb-3"
                />
                <span className="sr-only">
                  <BrandName />
                </span>
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center justify-center md:justify-end max-w-full">
            <div className={isAuthenticated && user ? 'hidden md:block' : ''}>
              <LocaleToggle currentLocale={currentLocale} onToggle={handleLocaleToggle} />
            </div>
            <Button variant="outline" asChild className="min-h-[44px]">
              <Link to="/join">
                <PixelIcon className="mr-2">🫂</PixelIcon>
                {t('home.join')}
              </Link>
            </Button>

            {isAuthenticated && user ? (
              <>
              <CoinBalance />
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="min-h-[44px] px-4 py-2 bg-card border-2 border-input text-foreground rounded-none flex items-center gap-2 font-display text-sm font-bold pixel-press hover:bg-accent transition-none"
                >
                  <PixelIcon className="text-lg">🙂</PixelIcon>
                  {user.username}
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-card z-50 pixel-frame overflow-hidden">
                    <div className="md:hidden px-4 py-3 border-b-2 border-border">
                      <LocaleToggle currentLocale={currentLocale} onToggle={handleLocaleToggle} />
                    </div>
                    <Link
                      to="/profile"
                      className="block w-full text-left px-4 py-3 font-display text-sm text-foreground hover:bg-accent transition-none"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {t('nav.profile')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 font-display text-sm text-destructive hover:bg-accent transition-none"
                    >
                      {t('auth.logout')}
                    </button>
                  </div>
                )}
              </div>
              </>
            ) : (
              <>
                <Button variant="ghost" asChild className="min-h-[44px]">
                  <Link to="/login">{t('auth.login')}</Link>
                </Button>
                <Button asChild className="min-h-[44px]">
                  <Link to="/register">{t('auth.register')}</Link>
                </Button>
              </>
            )}
          </div>
        </header>

        {/* Marketplace Grid */}
        <main className="flex-1">
        <section className="space-y-8">
          <div className="flex items-center gap-6">
            <h2 className="font-arcade text-xs uppercase tracking-widest text-secondary">
              {t('home.availableModules')}
            </h2>
            <div className="h-[3px] flex-grow bg-border" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading && (
              <>
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardHeader className="flex flex-row items-start justify-between">
                      <div className="w-12 h-12 bg-muted" />
                      <div className="space-y-2">
                        <div className="w-20 h-3 bg-muted" />
                        <div className="w-16 h-3 bg-muted" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="w-32 h-6 bg-muted mb-3" />
                      <div className="space-y-2">
                        <div className="w-full h-3 bg-muted" />
                        <div className="w-3/4 h-3 bg-muted" />
                      </div>
                    </CardContent>
                    <CardFooter>
                      <div className="w-full h-10 bg-muted" />
                    </CardFooter>
                  </Card>
                ))}
              </>
            )}

            {error && (
              <div className="col-span-full text-center py-12">
                <p className="text-destructive text-lg mb-4">{error}</p>
                <Button variant="destructive" onClick={() => window.location.reload()}>
                  {t('common.retry')}
                </Button>
              </div>
            )}

            {!loading && !error && games !== null && games.map((game) => (
              <Card
                key={game.id}
                className="group flex h-full flex-col transition-none hover:-translate-y-1 hover:shadow-pixel-lg"
              >
                {/* Narrow screens cannot fit icon, title and chips on one line,
                    so the icon spans two rows on the left and the title/chips
                    stack beside it. From md up the row wraps on demand: the
                    title uses `flex-auto`, not `flex-1`, so its flex basis is
                    its own content width and a long title pushes the chips onto
                    the next line instead of being squeezed. Both `min-w-0` below
                    are load-bearing: they are the only thing keeping a single
                    very long word from widening the card past the viewport. */}
                <CardHeader className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 space-y-0 md:flex md:flex-row md:flex-wrap">
                  <span className="row-span-2 flex h-11 w-11 shrink-0 items-center justify-center bg-muted text-2xl pixel-frame">
                    <EmojiText>{game.icon}</EmojiText>
                  </span>
                  <div className="flex min-w-0 items-center gap-2 md:flex-auto">
                    <h3 className="min-w-0 break-words text-xl font-giermek font-bold text-card-foreground text-outline-pixel text-ring-pixel">
                      {game.title}
                    </h3>
                    {game.free_tier_cutoff != null && (
                      <Badge variant="secondary">{t('home.demoBadge')}</Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 md:shrink-0">
                    <MetaChip icon="🫂" label={t('home.chipPlayersLabel')} value={game.players} />
                    <MetaChip icon="⚡" label={t('home.chipDurationLabel')} value={game.duration} />
                    <MetaChip icon="💎" label={t('home.chipCostLabel')} value={game.coin_cost} />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                    {game.description}
                  </p>
                </CardContent>
                {/* Header and description heights vary per game, so the call to
                    action is pinned to the bottom to keep it aligned across a
                    row of cards. */}
                <CardFooter className="mt-auto">
                  <Button
                    className="w-full flex items-center gap-2"
                    onClick={() => navigate(`/host/${game.id}`)}
                  >
                    <PixelIcon>▶</PixelIcon> {t('home.createRoom')}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </section>
        </main>

        {/* Footer */}
        <footer className="mt-32 pt-8 border-t border-border text-center">
          <span className="text-sm text-muted-foreground">
            {config.appName} v{config.version}
          </span>
          <nav className="mt-3 flex items-center justify-center gap-4 text-sm">
            <Link to="/regulamin" className="text-muted-foreground hover:text-foreground underline">
              {t('legal.termsLink')}
            </Link>
            <Link
              to="/polityka-prywatnosci"
              className="text-muted-foreground hover:text-foreground underline"
            >
              {t('legal.privacyLink')}
            </Link>
          </nav>
        </footer>
      </div>
    </div>
  )
}
